import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ProgressBar as ProgressBarComponent, Step } from 'react-step-progress-bar'
import classnames from 'classnames'

import './styles.scss'
import { isFirstStep, isSecondStep } from '../../../utils/helpers'
import { isMobile } from '../../../../shared/helpers/babHelpers'

const ProgressBar = ({
  percentage,
  freeProductsThreshold,
  secondFreeProductsThreshold,
  currentShippingThreshold,
  progressBarMax,
  priceDifferenceShipping,
  isPopin,
  freeGiftsSelectionAvailable,
  secondStepGiftsAvailable,
  samplesSettingTypesSkus,
  availableSamples,
  cartType,
  availableCartItem
}) => {
  const { t } = useTranslation()
  const cart = useSelector(({ cart }) => cart)

  const productsWithoutProgressBar = document.querySelector('meta[name="incentive_cart_hidden_shipping_sample_gift"]').content.split(',')
  if (cart.items.every(item => productsWithoutProgressBar.includes(item.sku))) return null

  const formatPrice = price => {
    const _price = String(price.toFixed(2))

    if (_price.slice(_price.length - 2) === '00' && isMobile()) return price.toFixed(0)
    return price.toFixed(2)
  }

  const progressBarSettings = {
    percent: Math.round(Number(percentage)),
    unfilledBackground: '#D9EEEB',
    filledBackground: '#63B8AD',
    height: '8px'
  }

  const getStepPositions = () => {
    if (!freeGiftsSelectionAvailable || !availableCartItem) {
      return [
        100
      ]
    }

    if (secondStepGiftsAvailable) {
      return [
        33,
        68,
        100
      ]
    }

    if (!secondStepGiftsAvailable) {
      return [
        50,
        100
      ]
    }
  }

  const cartTypeHasSamples = availableSamples.find(availableSample => {
    return samplesSettingTypesSkus[cartType].includes(availableSample.sku)
  })

  progressBarSettings.stepPositions = getStepPositions()

  const shippingStep = () => (
    <Step>
      {({ accomplished }) => (
        <div className={`indexedStep ${accomplished ? 'accomplished' : null}`}>
          <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/camion_93a4f05b-e863-410b-9b4b-591d40788631.svg?v=1623140960' />
          <div className='step-mark' />
          <div className='step-price'>
            {t('general:dynamicPrice', { price: formatPrice(Number(currentShippingThreshold / 100)) })}
          </div>
        </div>
      )}
    </Step>
  )

  const giftSteps = threshold => (
    <Step>
      {({ accomplished }) => (
        <div className={`indexedStep ${accomplished ? 'accomplished' : null} gift-step`}>
          <div className={classnames('gift-icons', { midStep: threshold <= freeProductsThreshold && threshold !== progressBarMax })}>
            <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/cadeau_d15c45ac-489c-4cb4-9180-ab9747e9cd6c.svg?v=1623757046' className='gift-icon' />
            {threshold <= freeProductsThreshold && threshold !== progressBarMax
              ? <div className='step-mark' />
              : <img src='https://cdn.shopify.com/s/files/1/1956/4693/files/Arrow_doodle_2_b72ea152-9b8a-437f-a7f5-e56606da34c2.svg?v=1623767261' />}
          </div>
          <div className='step-price-gift'>{t('general:dynamicPrice', { price: formatPrice(Number(threshold / 100)) })}</div>
        </div>
      )}
    </Step>
  )
  const steps = {
    shipping: () => shippingStep(),
    firstGift: () => giftSteps(freeProductsThreshold),
    secondGift: () => giftSteps(secondFreeProductsThreshold)
  }

  const renderProgressBar = () => {
    if (!freeGiftsSelectionAvailable || !availableCartItem) {
      return (
        <ProgressBarComponent {...progressBarSettings}>
          {steps.shipping()}
        </ProgressBarComponent>
      )
    } else if (!secondStepGiftsAvailable) {
      return (
        <ProgressBarComponent {...progressBarSettings}>
          {steps.shipping()}
          {steps.firstGift()}
        </ProgressBarComponent>
      )
    } else {
      return (
        <ProgressBarComponent {...progressBarSettings}>
          {steps.shipping()}
          {steps.firstGift()}
          {steps.secondGift()}
        </ProgressBarComponent>
      )
    }
  }

  /**
   * @property {String} product_type
   * @return {{__html: *}}
   */
  function renderProgressBarSentence () {
    const _isFirstStep = isFirstStep(cart.total_price)
    const _isSecondStep = isSecondStep(cart.total_price, secondStepGiftsAvailable)

    const belowShippingStep = cart.total_price < freeProductsThreshold && priceDifferenceShipping > 0
    const shippingFreeReached = cart.total_price < freeProductsThreshold && priceDifferenceShipping <= 0
    const cartFreebies = cart.items.filter(item => item.product_type === 'freebie').length

    if (belowShippingStep) {
      return {
        __html: t('slideCart:progressBarShippingNotfull', {
          price: t('general:dynamicPrice', { price: priceDifferenceShipping.toFixed(2) })
        })
      }
    }

    if (shippingFreeReached || !freeGiftsSelectionAvailable || !availableCartItem) {
      return { __html: t('slideCart:progressBarMetropolitanFranceShippingFree') }
    }

    if (_isFirstStep || !secondStepGiftsAvailable) {
      if (cartFreebies === 0) {
        const reminder = isPopin && cartTypeHasSamples
          ? 'progressBarSampleSelectionReminder'
          : 'progressBarfirstGiftUnlocked'

        return {
          __html: t(`slideCart:${reminder}`) }
      }

      if (secondStepGiftsAvailable && cartFreebies === 1) {
        return {
          __html: t('slideCart:progressBarSecondGiftUpcoming', {
            price: t('general:dynamicPrice', {
              price: priceDifferenceShipping.toFixed(2)
            })
          })
        }
      }
    }

    if (_isSecondStep) {
      return { __html: t('slideCart:progressBarSecondGiftUnlocked') }
    }

    if ((!secondFreeProductsThreshold && cart.total_price > freeProductsThreshold) ||
      (!secondStepGiftsAvailable && cartTypeHasSamples)
    ) {
      return {
        __html: t('slideCart:progressBarSampleSelectionReminder')
      }
    }
  }

  return (
    <div className='progress-bar-container'>
      <div className='progress-bar-title' dangerouslySetInnerHTML={renderProgressBarSentence()} />
      {renderProgressBar()}
    </div>
  )
}

export default ProgressBar
